import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { releasePointer, setPointer, subscribeFrame } from '@/lib/universe/store'

const INTERACTIVE = 'a, button, [role="button"], input, textarea, select, label, summary, [data-cursor="link"]'

// A faint thin ring that trails the cursor. It tightens over anything clickable,
// and the cursor itself carries a little mass that bends the stars and the disk.
const Cursor: FC = (): ReactNode => {
    const ringRef = useRef<HTMLDivElement>(null)
    const [enabled, setEnabled] = useState(false)

    useEffect(() => {
        const query = window.matchMedia('(hover: hover) and (pointer: fine)')
        const update = (): void => setEnabled(query.matches)

        update()
        query.addEventListener('change', update)

        return () => query.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        if (!enabled) return

        const ring = ringRef.current

        if (!ring) return

        const motion = { x: -100, y: -100, tx: -100, ty: -100, opacity: 0, targetOpacity: 0, seen: false }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType === 'touch') return

            motion.tx = event.clientX
            motion.ty = event.clientY
            motion.targetOpacity = 1

            if (!motion.seen) {
                motion.x = event.clientX
                motion.y = event.clientY
                motion.seen = true
            }

            setPointer(event.clientX, event.clientY)

            const target = event.target instanceof Element ? event.target.closest(INTERACTIVE) : null

            ring.dataset.active = target ? 'true' : 'false'
        }

        const onOut = (event: MouseEvent): void => {
            if (event.relatedTarget) return

            motion.targetOpacity = 0
            releasePointer()
        }

        const onBlur = (): void => {
            motion.targetOpacity = 0
            releasePointer()
        }

        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('mouseout', onOut)
        window.addEventListener('blur', onBlur)

        const unsubscribe = subscribeFrame((frame, dt) => {
            const follow = 1 - Math.exp(-dt * 13)

            motion.x += (motion.tx - motion.x) * follow
            motion.y += (motion.ty - motion.y) * follow
            motion.opacity += (motion.targetOpacity - motion.opacity) * (1 - Math.exp(-dt * 6))

            ring.style.transform = `translate3d(${motion.x.toFixed(1)}px, ${motion.y.toFixed(1)}px, 0) translate(-50%, -50%)`
            ring.style.opacity = motion.opacity.toFixed(3)
        })

        return () => {
            unsubscribe()
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('mouseout', onOut)
            window.removeEventListener('blur', onBlur)
            releasePointer()
        }
    }, [enabled])

    if (!enabled) return null

    return (
        <div
            ref={ringRef}
            aria-hidden='true'
            data-active='false'
            className='umbra-cursor pointer-events-none fixed left-0 top-0 z-[70] opacity-0'
        />
    )
}

export default Cursor
